import type { GameState } from '../types/game';
import type { CommandParser } from '../parser/command';

export class InputHandler {
  private inputEl: HTMLInputElement;
  private hintEl: HTMLElement | null;
  private history: string[] = [];
  private historyIndex = -1;
  private onSubmit: (input: string) => void;
  private getState: () => GameState;
  private parser: CommandParser;

  private tabMatches: string[] = [];
  private tabIndex = 0;
  private tabWordPos = -1;

  constructor(
    inputEl: HTMLInputElement,
    onSubmit: (input: string) => void,
    hintEl: HTMLElement | null | undefined,
    getState: () => GameState,
    parser: CommandParser,
  ) {
    this.inputEl = inputEl;
    this.hintEl = hintEl || null;
    this.onSubmit = onSubmit;
    this.getState = getState;
    this.parser = parser;
    this.bindEvents();
  }

  private bindEvents(): void {
    this.inputEl.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        const value = this.inputEl.value;
        if (value.trim()) {
          const existingIndex = this.history.indexOf(value);
          if (existingIndex !== -1) {
            this.history.splice(existingIndex, 1);
          }
          this.history.push(value);
          this.historyIndex = this.history.length;
        }
        this.onSubmit(value);
        this.inputEl.value = '';
        this.clearHint();
        this.resetTab();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.resetTab();
        if (this.history.length > 0 && this.historyIndex > 0) {
          this.historyIndex--;
          this.inputEl.value = this.history[this.historyIndex];
          this.clearHint();
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.resetTab();
        if (this.historyIndex < this.history.length - 1) {
          this.historyIndex++;
          this.inputEl.value = this.history[this.historyIndex];
        } else {
          this.historyIndex = this.history.length;
          this.inputEl.value = '';
        }
        this.clearHint();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        this.handleTab();
      } else {
        this.resetTab();
      }
    });
  }

  private handleTab(): void {
    const input = this.inputEl.value;
    const trimmed = input.trimEnd();
    const endsWithSpace = input.endsWith(' ');
    const parts = trimmed ? trimmed.split(/\s+/) : [];
    const wordPos = endsWithSpace ? parts.length : Math.max(0, parts.length - 1);
    const currentWord = parts[wordPos] || '';

    if (this.tabMatches.length > 1 && wordPos === this.tabWordPos && this.tabMatches.includes(currentWord)) {
      const idx = this.tabMatches.indexOf(currentWord);
      this.tabIndex = (idx + 1) % this.tabMatches.length;
      const match = this.tabMatches[this.tabIndex];
      if (wordPos === 0 || parts.length === 0) {
        this.inputEl.value = match + (endsWithSpace ? ' ' : '');
      } else {
        parts[wordPos] = match;
        this.inputEl.value = parts.join(' ') + (endsWithSpace ? ' ' : '');
      }
      this.updateHint();
      return;
    }

    const state = this.getState();
    const matches = this.parser.getCompletions(state, parts, wordPos);

    if (matches.length === 0) {
      this.resetTab();
      return;
    }

    this.tabMatches = matches;
    this.tabWordPos = wordPos;
    this.tabIndex = 0;

    const match = matches[0];
    if (wordPos === 0 || parts.length === 0) {
      this.inputEl.value = match + (endsWithSpace ? ' ' : '');
    } else {
      parts[wordPos] = match;
      this.inputEl.value = parts.join(' ') + (endsWithSpace ? ' ' : '');
    }

    if (matches.length > 1) {
      this.updateHint();
    } else {
      this.clearHint();
    }
  }

  private updateHint(): void {
    if (!this.hintEl || this.tabMatches.length <= 1) return;
    const remaining = this.tabMatches.filter((_, i) => i !== this.tabIndex);
    this.hintEl.textContent = remaining.length > 0 ? `  (${remaining.join(', ')})` : '';
  }

  private clearHint(): void {
    if (this.hintEl) this.hintEl.textContent = '';
  }

  private resetTab(): void {
    this.tabMatches = [];
    this.tabIndex = 0;
    this.tabWordPos = -1;
    this.clearHint();
  }

  focus(): void {
    this.inputEl.focus();
  }
}
